<script lang="ts" setup>
  import { storeToRefs } from 'pinia'
  import { Ref } from 'vue'
  import { useToggle } from '@vueuse/core'
  import { TaskBlockTextData, TaskBlockTypeId } from '~/model/TaskBlockData/TaskBlockData'
  import useMarkdown from '~/composables/Markdown/Markdown'
  import { computed, reactive, ref } from '#imports'
  import { useGameTaskStore } from '~/store/GameTask'
  import { GameTaskId } from '~/model/GameTask/GameTask'
  import { UseBemProps } from '~/composables/Bem/BemFacetOptions'
  import useBem from '~/composables/Bem/Bem'
  import { GameTaskStoreTextEntry } from '~/model/GameTaskStore/GameTaskStore'
  import useTranslation from '~/composables/Translation/Translation'
  import useNotification from '~/composables/Notification/Notification'

  interface Props extends TaskBlockTextData, UseBemProps {
    facets?: Array<string>
    taskId: GameTaskId
    type: TaskBlockTypeId
    title: string
    richText: string
    inputLabel?: string
  }

  const gameTaskStore = useGameTaskStore()
  const gameTaskStoreRefs = storeToRefs(gameTaskStore)

  const props = defineProps<Props>()
  const { t } = useTranslation()
  const { openNotification } = useNotification()
  const { bemAdd, bemFacets } = useBem('c-task-block-text', props, {})
  const { parseMarkdown } = useMarkdown()

  const store = gameTaskStoreRefs[props.taskId] as Ref<GameTaskStoreTextEntry>
  const model = reactive<GameTaskStoreTextEntry>({
    id: props.taskId,
    text: store.value.text ?? '',
    isCompleted: store.value.isCompleted,
  })

  const isEditing = ref<boolean>(!store.value.isCompleted)
  const toggleEditing = useToggle(isEditing)

  const rootClasses = computed<Array<string>>(() => {
    return [
      ...bemFacets.value,
      bemAdd(store.value.isCompleted ? 'is-completed' : ''),
      bemAdd(store.value.isCompleted ? 'is-completed' : '', '', 's-task-block'),
    ]
  })
  const hasUnsavedChanges = computed<boolean>(() => {
    if (!store.value.text && !model.text) {
      return false
    }

    return store.value.text !== model.text
  })

  const save = () => {
    store.value.text = model.text
    store.value.isCompleted = model.isCompleted
  }

  const onRequestEdit = () => {
    toggleEditing()
    save()
  }

  const onRequestComplete = () => {
    toggleEditing(false)
    model.isCompleted = true
    openNotification('overlay-notification-task-complete', 4000)
    save()
  }
</script>

<template>
  <div :class="rootClasses" class="c-task-block-text s-task-block">
    <div class="s-task-block__box">
      <TaskBlockHeader :is-completed="store.isCompleted" :type="type" :title="title" />
      <div class="s-task-block__content u-markdown-root" v-html="parseMarkdown(richText)" />

      <div class="s-task-block__interactions">
        <div class="c-task-block-text__fields">
          <template v-if="isEditing">
            <UiTextarea v-model="model.text">
              <template #label>
                {{ inputLabel || t('task-block-text.notes') }}
                <template v-if="hasUnsavedChanges">
                  ({{ t('task-block-text.unsaved-changes') }})
                </template>
              </template>
            </UiTextarea>
          </template>
          <template v-else>
            <span class="c-task-block-text__label">{{ t('task-block-text.notes') }}</span>
            <p class="c-task-block-text__notes">
              {{ store.text ? store.text : '&nbsp;' }}
            </p>
          </template>
        </div>
        <div class="c-task-block-text__actions">
          <button class="btn btn--primary btn--medium" @click="onRequestEdit">
            <template v-if="isEditing">{{ t('task-block-text.actions.notes-save') }}</template>
            <template v-else>{{ t('task-block-text.actions.notes-edit') }}</template>
          </button>
          <button
            v-if="!store.isCompleted"
            class="btn btn--primary btn--medium"
            @click="onRequestComplete"
          >
            {{ t('task-block-text.actions.complete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use '@nirazul/scss-utils' as utils;
  @use '/assets/scss/base/typography/typography' as type;
  @use '/assets/scss/util/color/color' as col;
  @use '/assets/scss/util/anaglyph/anaglyph' as anaglyph;
  @use '/assets/scss/util/box' as box;

  .c-task-block-text {
    grid-column: 3 / 11;
  }

  .c-task-block-text__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 32px;
    column-gap: 24px;
  }

  .c-task-block-text__label {
    @include type.h5;
    display: flex;
    margin-bottom: 4px;
  }

  .c-task-block-text__notes {
    color: col.$monochrome-lead;
    background-color: col.$monochrome-white;
    background-image: -webkit-linear-gradient(
      top,
      rgba(col.$monochrome-black, 0) 0%,
      rgba(col.$monochrome-black, 0) 95%,
      rgba(col.$monochrome-magnet, 0.1) 95%,
      rgba(col.$monochrome-magnet, 0.1) 100%
    );
    background-position: 50% 15px;
    background-repeat: repeat;
    background-size: 1rem 2.53rem;
    padding: 15px 16px 13px;
    white-space: pre-wrap;
  }
</style>
